/**
 * Portfolio super-planet aggregation.
 *
 * Collapses a multi-holding portfolio into a single synthetic position
 * located at the WEIGHTED-AVERAGE coordinates in factor space. Used by the
 * Portfolio Visualizer's "Combined" mode so you can see at a glance where
 * the book sits on Q × V × momentum vs the Alpha Zone ellipsoid.
 *
 * Weights come from the live portfolio weights (shares × price ÷ NAV);
 * cash is excluded from the weighting denominator (cash has no Q/V to
 * average — it's just neutral capital). For a 17-stock book with weights
 * summing to 0.92 (8% cash), each equity weight is renormalized to sum to
 * 1.0 across covered holdings before averaging.
 *
 * Holdings with missing Q or V are excluded from the average for that axis
 * (the other axes still get the data they have). If literally zero
 * holdings carry valid Q/V data, returns null.
 *
 * Pure function. No fs/network. Co-located tests in eps-revision-velocity
 * pattern.
 */

export interface SuperPlanetInput {
  symbol: string;
  qualityScore?: number | null;
  valuationScore?: number | null;
  momentumScore?: number | null;
  marketCap?: number | null;
  weight: number; // portfolio weight (0-1, equity-only or full-NAV — see below)
}

export interface SuperPlanetResult {
  qualityScore: number;
  valuationScore: number;
  momentumScore: number;
  /** Sum of (weight × marketCap) across covered holdings; used for sphere size. */
  syntheticMarketCap: number;
  /** Number of holdings that contributed to the average. */
  coveredCount: number;
  /** Total weight of holdings that contributed (after renormalization). */
  coveredWeight: number;
}

function isNum(v: number | null | undefined): v is number {
  return v != null && Number.isFinite(v);
}

/**
 * Compute the super-planet's coordinates by weighted-averaging factor
 * scores across holdings.
 *
 * The weighting denominator for each axis is the sum of weights of
 * holdings with VALID data for that axis. So if 16/17 holdings have a
 * quality score but only 14/17 have momentum, quality is averaged across
 * the 16 (renormalized) and momentum across the 14. This avoids dragging
 * the result toward zero when sparse fields are missing.
 *
 * Returns null if zero holdings have valid Q AND zero have valid V — i.e.
 * there's no meaningful aggregate to render.
 */
export function computeSuperPlanet(holdings: SuperPlanetInput[]): SuperPlanetResult | null {
  let qWeighted = 0, qWeight = 0;
  let vWeighted = 0, vWeight = 0;
  let mWeighted = 0, mWeight = 0;
  let mcWeighted = 0;
  let covered = 0;

  for (const h of holdings) {
    if (!isNum(h.weight) || h.weight <= 0) continue;
    let touched = false;
    if (isNum(h.qualityScore))   { qWeighted += h.qualityScore   * h.weight; qWeight += h.weight; touched = true; }
    if (isNum(h.valuationScore)) { vWeighted += h.valuationScore * h.weight; vWeight += h.weight; touched = true; }
    if (isNum(h.momentumScore))  { mWeighted += h.momentumScore  * h.weight; mWeight += h.weight; touched = true; }
    if (isNum(h.marketCap))      { mcWeighted += h.marketCap     * h.weight; }
    if (touched) covered++;
  }

  if (qWeight === 0 && vWeight === 0) return null;

  return {
    qualityScore:    qWeight > 0 ? qWeighted / qWeight : 0,
    valuationScore:  vWeight > 0 ? vWeighted / vWeight : 0,
    momentumScore:   mWeight > 0 ? mWeighted / mWeight : 0,
    syntheticMarketCap: mcWeighted, // raw $; caller can rescale for sphere sizing
    coveredCount: covered,
    coveredWeight: Math.max(qWeight, vWeight, mWeight),
  };
}
